import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useLanguage, languageNames, supportedLanguages } from '../../i18n';

const Header = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = React.useState(false);
  const { language, t, changeLanguage } = useLanguage();

  const navItems = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.skills'), href: '/skills' },
    { label: t('nav.missions'), href: '/missions' },
    { label: t('nav.employers'), href: '/employers' },
    { label: t('nav.certifications'), href: '/certifications' },
    { label: t('nav.projects'), href: '/projects' },
    { label: t('nav.contact'), href: '/contact' },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link 
            to="/" 
            className="font-mono text-sm lg:text-base font-medium text-slate-800 tracking-wider uppercase hover:text-blue-600"
          >
            Joel.Dossa
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`font-mono text-xs uppercase tracking-wider transition-colors ${
                  isActive(item.href) 
                    ? 'text-blue-600' 
                    : 'text-slate-500 hover:text-blue-600'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="font-mono text-xs uppercase tracking-wider rounded-none hover:bg-slate-100 gap-1.5"
                >
                  <Globe className="w-4 h-4" />
                  {languageNames[language]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none min-w-[80px]">
                {supportedLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang}
                    onClick={() => changeLanguage(lang)}
                    className={`font-mono text-xs uppercase tracking-wider cursor-pointer ${
                      language === lang ? 'text-blue-600 bg-blue-50' : ''
                    }`}
                  >
                    {languageNames[lang]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Menu */}
          <div className="flex lg:hidden items-center gap-2">
            {/* Mobile Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-none">
                  <Globe className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none min-w-[80px]">
                {supportedLanguages.map((lang) => (
                  <DropdownMenuItem
                    key={lang}
                    onClick={() => changeLanguage(lang)}
                    className={`font-mono text-xs uppercase tracking-wider cursor-pointer ${
                      language === lang ? 'text-blue-600 bg-blue-50' : ''
                    }`}
                  >
                    {languageNames[lang]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-none">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-80 bg-white">
                <nav className="flex flex-col gap-6 mt-12">
                  {navItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`font-mono text-sm uppercase tracking-wider py-2 border-b border-slate-100 ${
                        isActive(item.href) 
                          ? 'text-blue-600' 
                          : 'text-slate-800 hover:text-blue-600'
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
